import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Eye } from "lucide-react";
import { initials, PageHeader } from "@/components/common";

const STATUS = {
  hijau: { label: "Baik", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  kuning: { label: "Perlu Perhatian", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  merah: { label: "Bermasalah", cls: "bg-rose-100 text-rose-800", dot: "bg-rose-500" },
};

export function statusOf(s) {
  const poin = s.poin ?? 100;
  const avg = [s.ngaji_persen, s.departemen_persen, s.kuliah_persen].filter((v) => v != null);
  const mean = avg.length ? avg.reduce((a, b) => a + b, 0) / avg.length : null;
  if (poin < 70 || (mean != null && mean < 40)) return "merah";
  if (poin < 90 || (mean != null && mean < 60)) return "kuning";
  return "hijau";
}

export default function PengasuhView({ onOpenDetail }) {
  const { data: santri = [] } = useQuery({ queryKey: ["santri"], queryFn: async () => (await api.get("/santri")).data });
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const [q, setQ] = useState("");
  const [rib, setRib] = useState("all");
  const [dep, setDep] = useState("all");
  const [st, setSt] = useState("all");

  const rows = useMemo(() => santri
    .map((s) => ({ ...s, _st: statusOf(s) }))
    .filter((s) => (rib === "all" || s.ribath === rib) && (dep === "all" || s.departemen === dep) && (st === "all" || s._st === st) &&
      (s.nama.toLowerCase().includes(q.toLowerCase()) || (s.no_induk_pondok || "").includes(q))), [santri, q, rib, dep, st]);

  const counts = useMemo(() => santri.reduce((a, s) => { const k = statusOf(s); a[k] = (a[k] || 0) + 1; return a; }, {}), [santri]);

  return (
    <div className="space-y-5" data-testid="pengasuh-view">
      <PageHeader icon={Eye} title="Pemantauan Santri" subtitle={`${santri.length} santri · klik kartu untuk membuka profil & menambah catatan pembimbing.`} />
      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="pengasuh-status-grid">
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} onClick={() => setSt(st === k ? "all" : k)} data-testid={`pengasuh-filter-${k}`}
            className={`flex flex-col items-center justify-center gap-1 rounded-2xl border px-2 py-3 text-center transition-colors min-h-[80px] ${st === k ? "ring-2 ring-emerald-400" : ""} ${v.cls}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${v.dot}`} />
            <span className="text-xs font-medium leading-tight">{v.label}</span>
            <span className="text-lg font-extrabold leading-none">{counts[k] || 0}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap">
        <div className="relative w-full sm:w-auto"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
          <Input data-testid="pengasuh-search" placeholder="Cari nama / no induk..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full sm:w-56 rounded-xl bg-white/60 h-10" /></div>
        <Select value={rib} onValueChange={setRib}><SelectTrigger className="w-full sm:w-44 rounded-xl bg-white/60 h-10" data-testid="pengasuh-ribath-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Ribath</SelectItem>{(options.ribath || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
        <Select value={dep} onValueChange={setDep}><SelectTrigger className="w-full sm:w-48 rounded-xl bg-white/60 h-10" data-testid="pengasuh-dep-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Departemen</SelectItem>{(options.departemen || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {rows.map((s) => (
          <button key={s.id} onClick={() => onOpenDetail(s.id)} data-testid={`pengasuh-card-${s.no_induk_pondok}`}
            className="glass glass-hover rounded-2xl p-4 text-left flex items-start gap-3 min-h-[88px]">
            <Avatar className="h-11 w-11 shrink-0 ring-2 ring-white"><AvatarImage src={s.foto} /><AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm">{initials(s.nama)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-emerald-950 leading-tight break-words">{s.nama}</p>
              <p className="text-xs text-slate-500 break-words">{s.no_induk_pondok} · {s.ribath || "-"} · {s.departemen || "-"}</p>
              <div className="flex items-center gap-2 mt-1.5"><Badge className={`border-0 text-[10px] ${STATUS[s._st].cls}`}>{STATUS[s._st].label}</Badge><span className="text-[10px] text-slate-400">Poin {s.poin}</span></div>
            </div>
            <Eye className="h-4 w-4 text-slate-300 shrink-0" />
          </button>
        ))}
        {rows.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">Tidak ada santri yang cocok.</p>}
      </div>
    </div>
  );
}
